import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Flame } from "lucide-react";
import { SITE } from "@/constants/testIds";
import { apiClient } from "@/lib/api";
import { SECTIONS } from "@/lib/sections";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { ReportCard } from "@/components/site/ReportCard";
import { HomeHero } from "@/components/site/HomeHero";
import { HomeSectionStrip } from "@/components/site/HomeSectionStrip";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.06, ease: "easeOut" },
  }),
};

// Hoisted so framer-motion gets a stable object reference across re-renders.
const CARD_VIEWPORT = { once: true, margin: "-60px" };

export default function HomePage() {
  const [latest, setLatest] = useState([]);
  const [trending, setTrending] = useState([]);
  const [counts, setCounts] = useState({});
  const [sectionReports, setSectionReports] = useState({});

  useEffect(() => {
    document.title = "KIRA Score — Research Before Returns";
  }, []);

  useEffect(() => {
    Promise.all([
      apiClient.get("/public/reports", { params: { limit: 8 } }),
      apiClient.get("/public/trending", { params: { limit: 6 } }),
      apiClient.get("/public/sections"),
    ])
      .then(([latestRes, trendingRes, countsRes]) => {
        setLatest(latestRes.data);
        setTrending(trendingRes.data);
        setCounts(countsRes.data);
      })
      .catch(() => {});
    Promise.all(
      SECTIONS.map((s) =>
        apiClient
          .get("/public/reports", { params: { section: s.key, limit: 4 } })
          .then(({ data }) => [s.key, data])
          .catch(() => [s.key, []]),
      ),
    ).then((rows) => setSectionReports(Object.fromEntries(rows)));
  }, []);

  return (
    <div className="min-h-screen bg-kira-bg text-white">
      <SiteNav hideSearch />

      <HomeHero counts={counts} />

      {/* Trending Scores */}
      {trending.length > 0 && (
        <section data-testid={SITE.trendingSection} className="border-b border-kira-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
            <div className="flex items-end justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-md bg-kira-gold/10 border border-kira-gold/30">
                  <Flame className="h-4 w-4 text-kira-gold" />
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-kira-textMuted mb-0.5">Trending</p>
                  <h2 className="font-chivo text-lg font-bold text-white">Top KIRA Scores</h2>
                </div>
              </div>
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-kira-textMuted/70 hidden sm:block">
                Highest scoring published research
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {trending.slice(0, 6).map((r, i) => (
                <motion.div key={r.id} variants={fadeUp} initial="hidden" whileInView="show" viewport={CARD_VIEWPORT} custom={i}>
                  <ReportCard report={r} />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Latest Research */}
      <section data-testid={SITE.latestResearch} className="border-b border-kira-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
          <div className="flex items-end justify-between mb-6">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-kira-textMuted mb-0.5">Latest</p>
              <h2 className="font-chivo text-lg font-bold text-white">Fresh Research</h2>
            </div>
          </div>
          {latest.length === 0 ? (
            <div className="rounded-lg border border-dashed border-kira-border py-16 text-center">
              <p className="text-sm text-kira-textMuted">New KIRA Score research is being prepared. First reports publish soon.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {latest.map((r, i) => (
                <motion.div key={r.id} variants={fadeUp} initial="hidden" whileInView="show" viewport={CARD_VIEWPORT} custom={i}>
                  <ReportCard report={r} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Section strips */}
      {SECTIONS.map((s) => (
        <HomeSectionStrip key={s.key} section={s} reports={sectionReports[s.key] || []} />
      ))}

      {/* About */}
      <section data-testid={SITE.aboutSection} className="border-t border-kira-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 grid md:grid-cols-2 gap-10">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-kira-gold mb-2">About</p>
            <h2 className="font-chivo text-lg font-bold text-white">About KIRA Score</h2>
          </div>
          <div className="space-y-4 text-kira-textMuted text-sm leading-relaxed">
            <p>
              KIRA Score is India&apos;s proprietary equity scoring platform. Every company we cover receives a
              <span className="text-white font-medium"> KIRA Score</span> — a single 0–100 number that condenses
              fundamental, technical and thematic analysis into one comparable figure.
            </p>
            <p>
              We publish across four desks: listed Indian equities, unlisted companies, upcoming IPOs and
              commodities. Each report ships with the full PDF research note, free to read and download.
            </p>
            <p className="font-mono text-xs text-kira-textMuted/70">
              Research is for informational purposes only — not investment advice.
            </p>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
